import { getAuthToken, isLoggedIn } from "@/utils/authUtil";
import { getRouteByPerfilNumber, ROTA_BASE, ROTA_PADRAO, ROTAS_TRAVADAS } from "@/utils/perfilRouteUtil";

import store from "@/store";
import { isHealthNativeBridgeAvailable } from "@/utils/healthNativeBridge";

const normalizePath = path => {
  let normalizedPath = path;
  if (normalizedPath.includes("?")) {
    normalizedPath = normalizedPath.substring(0, normalizedPath.indexOf("?"));
  }
  if (normalizedPath.endsWith("/")) {
    normalizedPath = normalizedPath.substring(0, normalizedPath.length - 1);
  }
  return normalizedPath;
};

const hasSelectedLink = () => {
  const { vinculo, operacao, perfil } = store.state.saude;
  return vinculo !== "" && perfil !== 0 && operacao !== 0;
};

const getRouteByCurrentPerfil = () => {
  const { operacao, perfil } = store.state.saude;
  return operacao > 0 && perfil > 0 ? getRouteByPerfilNumber(operacao, perfil) : "";
};

const hasAuthRedirectQuery = to =>
  (to.query?.vinculo && to.query?.login && to.query?.usuario) || (to.query?.cd1 && to.query?.cd2 && to.query?.cd4);

export const buildLoginRedirect = (to, routeByPerfil) => {
  const isBaseRoute =
    to.fullPath === "" || to.fullPath === "/" || to.fullPath === "/vinculos" || to.fullPath === routeByPerfil;
  if (isBaseRoute) return { path: ROTA_PADRAO };
  return {
    path: ROTA_PADRAO,
    query: {
      nextUrl: to.fullPath
    }
  };
};

const buildRedirectRoute = to => {
  const authQuery = {
    nextUrl: to.path
  };
  Object.keys(to.query).forEach(k => {
    authQuery[k] = to.query[k];
  });
  return {
    name: "redirect",
    params: authQuery
  };
};

const hasCartaPermanenciaDisabled = to =>
  to.path === "/carta-permanencia" && process.env.VUE_APP_ENABLE_CARTA_PERMANENCIA !== "true";

const shouldBlockOfflineRoute = to => !to.meta.offline && !navigator.onLine;

const isAllowedWithoutSessionRules = to => {
  if (to.name === "logout") return true;
  return to.name === "login" && store.state.accessManagement.appLocked;
};

export const resolveLockedRoute = (to, state, fullPath) => {
  for (const rotaTravada of ROTAS_TRAVADAS(state)) {
    const canBypass = (rotaTravada.path === "/inadimplencia" && to.path === "/faturas") || to.path.includes("biometry");
    if (canBypass) return null;
    if (!rotaTravada.condiction) continue;
    if (to.path.includes(rotaTravada.path)) return null;
    return {
      path: rotaTravada.path,
      query: rotaTravada.nextPath
        ? {
            nextUrl: fullPath
          }
        : undefined
    };
  }
  return null;
};

const registerRouteAccess = (to, from, routeByPerfil) => {
  const vinculo = store.state.saude.vinculo;
  const shouldRegister =
    vinculo !== "" &&
    to?.name &&
    from?.name &&
    (from.fullPath === "/" ||
      from.fullPath === routeByPerfil ||
      (!to.name.includes(from.name) && !from.name.includes(to.name)));

  if (!shouldRegister) return;

  if (to.meta?.registerAccess?.servico && to.meta?.registerAccess?.funcionalidade) {
    store.dispatch("myAccount/registerAccess", {
      servico: to.meta.registerAccess.servico,
      funcionalidade: to.meta.registerAccess.funcionalidade,
      id: null
    });
  }
  if (to.meta?.idClassifica) {
    store.dispatch("myAccount/registerNavigation", {
      classificacao: to.meta?.idClassifica
    });
  }
};

export const hasAccessPermition = async to => {
  if (to.meta.allowAnonymous) return true;
  if (to.meta.checkPathDisabled) return true;

  if (
    (to.path === "/carta" || to.path === "/seguro-viagem/carta") &&
    store.getters["saude/hasExclusiveTravelInsurance"]
  ) {
    return false;
  }

  const menu = store.state.saude.menu ?? [];
  const toPath = normalizePath(to.path);
  const validPath = to.meta.validPath?.length ? to.meta.validPath : [toPath];
  let valid = validPath.some(p => menu.some(m => m.View?.startsWith(p)));
  if (!valid) {
    const menusFavoritos = store.state.saude.menusFavoritos ?? [];
    valid = validPath.some(p => menusFavoritos.some(m => m.View?.startsWith(p)));
  }
  if (valid && to.meta.validAction) {
    valid = await store.dispatch(to.meta.validAction);
  }
  return valid;
};

const redirectToLoginWhenNeeded = (to, next, isLogged, routeByPerfil) => {
  if (to.meta.allowAnonymous || (isLogged && hasSelectedLink())) return false;
  next(buildLoginRedirect(to, routeByPerfil));
  return true;
};

const redirectLoggedLogin = (to, next, routeByPerfil) => {
  if (to.name !== "login" || !isLoggedIn(true)) return false;

  if (hasSelectedLink()) {
    next({ path: routeByPerfil });
  } else {
    next();
  }
  return true;
};

const ensureAuthenticatedContext = async () => {
  if (!globalThis.window.$axios.hasAuthToken()) {
    globalThis.window.$axios.setAuthToken(getAuthToken());
  }
  if (!store.state.saude.hydrated && !store.state.saude.hydrating) {
    await store.dispatch("saude/hydrate");
  }
  return store.dispatch("myAccount/sessionState");
};

const redirectWhenPermissionIsDenied = async (to, from, next) => {
  const hasPermition = await hasAccessPermition(to);
  if (hasPermition) return false;

  const initRoute = await store.dispatch("saude/fetchInitRoute");
  next(from?.path?.startsWith(initRoute) ? ROTA_BASE : initRoute);
  return true;
};

async function beforeEachRouteGuard(to, from, next) {
  if (hasCartaPermanenciaDisabled(to)) {
    next(ROTA_BASE);
    return;
  }

  if (to.meta.requiresNativeCapability === "health") {
    const allowed = await store.dispatch("healthIntegration/initializeHealth");
    if (!allowed && !(to.meta.allowWebFallback && !isHealthNativeBridgeAvailable())) {
      next(ROTA_BASE);
      return;
    }
  }

  if (shouldBlockOfflineRoute(to)) {
    globalThis.window.dispatchEvent(new Event("offline"));
    next(false);
    return;
  }

  if (isAllowedWithoutSessionRules(to)) {
    next();
    return;
  }

  const routeByPerfil = getRouteByCurrentPerfil();
  if (redirectLoggedLogin(to, next, routeByPerfil)) return;

  const isLogged = isLoggedIn();
  if (isLogged && to.name === "vinculos" && !hasSelectedLink()) {
    next();
    return;
  }

  if (to.name !== "redirect" && to.name !== "readonly.access" && hasAuthRedirectQuery(to)) {
    await store.dispatch("logoutUser");
    next(buildRedirectRoute(to));
    return;
  }

  if (redirectToLoginWhenNeeded(to, next, isLogged, routeByPerfil)) return;

  if (!isLogged) {
    next();
    return;
  }

  const sessaoValida = await ensureAuthenticatedContext();
  if (!sessaoValida || to.name === "logout") {
    next("/logout");
    return;
  }

  const lockedRoute = resolveLockedRoute(to, store.state, to.fullPath);
  if (lockedRoute) {
    next(lockedRoute);
    return;
  }

  if (await redirectWhenPermissionIsDenied(to, from, next)) return;

  registerRouteAccess(to, from, routeByPerfil);
  next();
}

export default beforeEachRouteGuard;
